
import { Helmet } from 'react-helmet-async';

// Canonical / og:url ALWAYS point to the production URL, regardless of which
// environment serves the page. This is intentional per SEO spec — test/staging
// pages canonical back to production so they don't compete in search rankings.
// Host is WWW: the site serves on www.sportskinetic.ai, so canonical/og:url/hreflang
// must use www or Google files the served page as an "alternate" of a non-www canonical.
const PRODUCTION_URL = 'https://www.sportskinetic.ai';
const DEFAULT_OG_IMAGE = `${PRODUCTION_URL}/assets/facebook-og-image0926.png`;

const PRODUCTION_HOSTNAMES = ['sportskinetic.ai', 'www.sportskinetic.ai'];
const isTestEnvironment = () => {
    if (typeof window !== 'undefined') return !PRODUCTION_HOSTNAMES.includes(window.location.hostname);
    // SSR: no window to read. lambda/ssr-handler.js sets this global (from SSR_SITE_URL)
    // before rendering, so non-prod deployments still emit noindex in the raw HTML instead
    // of only after client hydration.
    return typeof globalThis !== 'undefined' && globalThis.__SSR_NON_PROD__ === true;
};

export default function SeoHelmet({
    title = 'Football Predictions, AI News & Player Insights | SportsKinetic',
    description = 'SportsKinetic provides football predictions, AI-powered news summaries, player insights and fixture analysis in one platform.',
    slug = '',
    keywords = '',
    image,
    imageWidth: imageWidthProp,
    imageHeight: imageHeightProp,
    // twitter:image defaults to the same image/dimensions as og:image — pass these only for
    // routes (the SSR'd ones, via routeMeta.js's getSeoRoute()) that bake a distinct Twitter
    // crop, so the server-rendered <head> and the client-hydrated <head> match exactly.
    twitterImage: twitterImageProp,
    twitterImageWidth: twitterImageWidthProp,
    twitterImageHeight: twitterImageHeightProp,
    type = 'website',
    firstName,
    lastName,
    jsonLd,
    noindex = false,
    nofollow = false,
}) {
    const canonicalUrl = `${PRODUCTION_URL}${slug}`;

    const resolveUrl = (img) => (img && !img.startsWith('http') ? `${PRODUCTION_URL}${img}` : img);

    const ogImage = resolveUrl(image) || DEFAULT_OG_IMAGE;
    const shouldNoIndex = noindex || isTestEnvironment();
    // Robots policy:
    // - Non-prod (dev/UAT): block everything - "noindex, nofollow" - so staging never ranks.
    // - Prod + explicit noindex: stay out of the index but respect nofollow. Public utility/
    //   funnel pages (login, subscribe, sign-up) keep "follow" so link equity flows; private
    //   app pages pass nofollow so crawlers don't follow links deeper into authenticated areas.
    // - Everything else (public content: home, guides, news, fixtures, leagues): "index, follow".
    const noFollow = nofollow || isTestEnvironment();
    const robotsContent = shouldNoIndex
        ? `noindex, ${noFollow ? 'nofollow' : 'follow'}`
        : 'index, follow';

    const isDefaultImage = ogImage === DEFAULT_OG_IMAGE;
    const imageWidth = imageWidthProp ?? (isDefaultImage ? '1732' : '800');
    const imageHeight = imageHeightProp ?? (isDefaultImage ? '908' : '418');

    const twitterImage = resolveUrl(twitterImageProp) || ogImage;
    const isDefaultTwitterImage = twitterImage === DEFAULT_OG_IMAGE;
    const twitterImageWidth = twitterImageWidthProp ?? (twitterImageProp ? (isDefaultTwitterImage ? '1732' : '800') : imageWidth);
    const twitterImageHeight = twitterImageHeightProp ?? (twitterImageProp ? (isDefaultTwitterImage ? '908' : '418') : imageHeight);

    const imageExtension = ogImage.split('.').pop().split('?')[0].toLowerCase();
    const mimeTypes = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };
    const imageType = mimeTypes[imageExtension];

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="robots" content={robotsContent} />
            <link rel="canonical" href={canonicalUrl} />

            {/* hreflang — we serve identical English content to US and UK audiences (no
                locale-specific URLs), so every language variant points at the canonical URL.
                x-default catches every other locale. Page is relevant to both en-gb and en-us searchers. */}
            <link rel="alternate" hrefLang="en-gb" href={canonicalUrl} />
            <link rel="alternate" hrefLang="en-us" href={canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

            <meta property="og:site_name" content="SportsKinetic" />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta name="twitter:title" content={title} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content={imageWidth} />
            <meta property="og:image:height" content={imageHeight} />
            {imageType && <meta property="og:image:type" content={imageType} />}
            <meta property="og:image:alt" content={title} />
            <meta property="og:locale" content="en_GB" />

            {type === 'profile' && firstName && <meta property="profile:first_name" content={firstName} />}
            {type === 'profile' && lastName && <meta property="profile:last_name" content={lastName} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@sk_ai_official" />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={twitterImage} />
            <meta name="twitter:image:width" content={twitterImageWidth} />
            <meta name="twitter:image:height" content={twitterImageHeight} />
            <meta name="twitter:image:alt" content={title} />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}
